import { isInvoiceMail, MailDelivery } from '../../types/mail';
import { LineItem } from '../../types/invoices';
import { InvoiceItemTypeData } from '../../enums/InvoiceItemType';
import {
  UnitAddOnPricing,
  NoticeAddOnPricingConfig,
  makeUnitAddOnPricing,
  DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS
} from './types';

/**
 * Computes per-copy ground shipping for invoice copies.
 * Returns undefined when there are no copies.
 */
export function computeInvoiceShipping(
  invoiceRecords: MailDelivery[],
  config: NoticeAddOnPricingConfig
): UnitAddOnPricing | undefined {
  const totalCopies = invoiceRecords.reduce(
    (sum, m) => sum + (m.copies ?? 0),
    0
  );
  if (totalCopies === 0) {
    return undefined;
  }
  return makeUnitAddOnPricing(
    config.groundShippingFeeInCents ?? DEFAULT_GROUND_SHIPPING_FEE_IN_CENTS,
    totalCopies
  );
}

export const MAILED_INVOICE_LINE_ITEM_DESCRIPTION = 'Mailed invoice';

export function isMailedInvoiceAddOnLineItem(
  lineItem: Pick<LineItem, 'description' | 'type'>
): boolean {
  return (
    lineItem.type === InvoiceItemTypeData.shipping_fee_oop.value &&
    lineItem.description === MAILED_INVOICE_LINE_ITEM_DESCRIPTION
  );
}

/**
 * An invoice billed outside Column is never mailed: handleInvoiceCreationMailing
 * returns before it initiates the invoice mail docs, so the copies must not be billed.
 */
export function excludeUnmailedInvoiceCopies(
  mail: MailDelivery[],
  invoiceOutsideColumn: boolean | undefined
): MailDelivery[] {
  if (!invoiceOutsideColumn) {
    return mail;
  }
  return mail.filter(m => !isInvoiceMail(m));
}

/** The line-item counterpart to excludeUnmailedInvoiceCopies; the two must agree. */
export function excludeUnbilledInvoiceMailingLineItems<
  T extends Pick<LineItem, 'description' | 'type'>
>(lineItems: T[], invoiceOutsideColumn: boolean | undefined): T[] {
  if (!invoiceOutsideColumn) {
    return lineItems;
  }
  return lineItems.filter(lineItem => !isMailedInvoiceAddOnLineItem(lineItem));
}
